/**
 * MarketingPivotTable — reusable table for marketing report data.
 *
 * Shares the same visual language as the product PivotTable:
 *   - Heatmap & bar cell decoration
 *   - Sorting, pagination, CSV export, copy to clipboard
 *   - Sticky dimension column + sticky header/footer
 *   - Totals row
 *
 * Marketing-specific features:
 *   - ROAS colour coding (green > 1, red < 1)
 *   - Per-metric number formatting (currency, percentages, etc.)
 */
import { useMemo, useState, useCallback } from 'react';
import {
  Download,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ChevronLeft,
  ChevronRight,
  Copy,
} from 'lucide-react';
import { cn } from '@/lib/cn';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface MarketingPivotTableProps {
  rows: Array<Record<string, unknown>>;
  /** Which key in each row holds the breakdown value (e.g. campaign_name) */
  breakdownKey: string;
  /** Which metric keys to show (e.g. ['installs', 'revenue', 'cost']) */
  displayMetrics: string[];
  className?: string;
  /** Comparison period rows (same shape as rows, different date range) */
  comparisonRows?: Array<Record<string, unknown>>;
}

type SortDir = 'asc' | 'desc' | null;
interface SortState {
  key: string;
  dir: SortDir;
}

// ─── Metric display helpers ─────────────────────────────────────────────────

const METRIC_LABELS: Record<string, string> = {
  cost: 'Cost (₺)',
  revenue: 'Revenue (₺)',
  refunds: 'Refunds (₺)',
  refund_rate: 'Refund Rate (%)',
  impressions: 'Ad Impressions',
  clicks: 'Ad Clicks',
  installs: 'Installs',
  cpi: 'CPI',
  roas: 'ROAS',
  ltv: 'LTV',
  cvr: 'CVR (%)',
  pcvr: 'PCVR (%)',
  cpp: 'CPP',
  total_purchases: 'Purchases',
  initial_purchases: 'Initial',
  renewals: 'Renewals',
  special_offer_purchases: 'Special Offers',
  unique_purchasers: 'Unique Purchasers',
  landing_page_visits: 'Landing Page Visits',
  landing_page_clicks: 'Landing Page Clicks',
};

const METRIC_FORMAT: Record<string, (v: number) => string> = {
  cost: (v) => `₺${fmtNum(v)}`,
  revenue: (v) => `₺${fmtNum(v)}`,
  refunds: (v) => `₺${fmtNum(v)}`,
  refund_rate: (v) => `${v.toFixed(1)}%`,
  cpi: (v) => `₺${v.toFixed(2)}`,
  cpp: (v) => `₺${v.toFixed(2)}`,
  roas: (v) => v.toFixed(2),
  ltv: (v) => `₺${v.toFixed(2)}`,
  cvr: (v) => `${v.toFixed(1)}%`,
  pcvr: (v) => `${v.toFixed(1)}%`,
};

/** Map user-facing metric key to the actual row field key */
function metricToRowKey(metric: string): string {
  const map: Record<string, string> = {
    cost: 'cost_try',
    revenue: 'total_revenue_try',
    refunds: 'refunds_try',
  };
  return map[metric] ?? metric;
}

function fmtNum(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n % 1 === 0 ? String(n) : n.toFixed(2);
}

function formatMetric(key: string, value: number): string {
  const formatter = METRIC_FORMAT[key];
  if (formatter) return formatter(value);
  return value % 1 === 0 ? String(value) : value.toFixed(2);
}

// ─── Heatmap / Bar helpers (matching PivotTable) ────────────────────────────

function getHeatmapStyle(
  value: number | undefined | null,
  min: number,
  max: number,
): { bg: string; text: string } {
  if (value == null || min === max) return { bg: 'transparent', text: '#334155' };
  const t = Math.max(0, Math.min(1, (value - min) / (max - min)));
  const alpha = 0.08 + t * 0.47;
  return { bg: `rgba(99, 102, 241, ${alpha})`, text: t > 0.65 ? '#1e1b4b' : '#334155' };
}

function getBarWidth(value: number | null | undefined, max: number): number {
  if (value == null || max === 0) return 0;
  return Math.max(2, Math.min(100, (Math.abs(value) / Math.abs(max)) * 100));
}

const PAGE_SIZE_OPTIONS = [25, 50, 100] as const;

// ─── Component ──────────────────────────────────────────────────────────────

export function MarketingPivotTable({
  rows: rawRows,
  breakdownKey,
  displayMetrics,
  className,
  comparisonRows,
}: MarketingPivotTableProps) {
  const [sort, setSort] = useState<SortState>({ key: '', dir: null });
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState<number>(50);
  const [heatmapEnabled, setHeatmapEnabled] = useState(false);
  const [barsEnabled, setBarsEnabled] = useState(true);

  // ── Sorting ────────────────────────────────────────────────────────────
  const toggleSort = useCallback(
    (key: string) => {
      setSort((prev) => {
        if (prev.key !== key) return { key, dir: 'desc' };
        if (prev.dir === 'desc') return { key, dir: 'asc' };
        return { key: '', dir: null };
      });
    },
    [],
  );

  const sortedRows = useMemo(() => {
    if (!sort.key || !sort.dir) return rawRows;
    return [...rawRows].sort((a, b) => {
      let aVal: string | number;
      let bVal: string | number;

      if (sort.key === '__breakdown') {
        aVal = String(a[breakdownKey] ?? '');
        bVal = String(b[breakdownKey] ?? '');
        return sort.dir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }

      const rowKey = metricToRowKey(sort.key);
      aVal = Number(a[rowKey] ?? a[sort.key] ?? 0);
      bVal = Number(b[rowKey] ?? b[sort.key] ?? 0);
      return sort.dir === 'asc' ? (aVal as number) - (bVal as number) : (bVal as number) - (aVal as number);
    });
  }, [rawRows, sort, breakdownKey]);

  // ── Column stats (for heatmap / bars) ──────────────────────────────────
  const colStats = useMemo(() => {
    const stats: Record<string, { min: number; max: number; total: number }> = {};
    for (const m of displayMetrics) {
      const rowKey = metricToRowKey(m);
      let min = Infinity;
      let max = -Infinity;
      let total = 0;
      for (const row of rawRows) {
        const v = Number(row[rowKey] ?? 0);
        if (v < min) min = v;
        if (v > max) max = v;
        total += v;
      }
      stats[m] = { min: min === Infinity ? 0 : min, max: max === -Infinity ? 0 : max, total };
    }
    return stats;
  }, [rawRows, displayMetrics]);

  // ── Comparison lookup: breakdownValue → { metricKey → value } ─────────
  const cmpLookup = useMemo(() => {
    if (!comparisonRows || comparisonRows.length === 0) return null;
    const map = new Map<string, Record<string, number>>();
    for (const row of comparisonRows) {
      const key = String(row[breakdownKey] ?? '');
      if (!key) continue;
      const bucket: Record<string, number> = {};
      for (const m of displayMetrics) {
        const rowKey = metricToRowKey(m);
        bucket[rowKey] = Number(row[rowKey] ?? 0);
      }
      map.set(key, bucket);
    }
    return map;
  }, [comparisonRows, breakdownKey, displayMetrics]);

  // ── Pagination ─────────────────────────────────────────────────────────
  const totalPages = Math.ceil(sortedRows.length / pageSize);
  const pagedRows = useMemo(
    () => sortedRows.slice(page * pageSize, (page + 1) * pageSize),
    [sortedRows, page, pageSize],
  );

  // ── CSV export ─────────────────────────────────────────────────────────
  const handleExport = useCallback(() => {
    const header = [breakdownKey, ...displayMetrics.map((m) => METRIC_LABELS[m] ?? m)];
    const csvRows = sortedRows.map((row) => {
      const bd = String(row[breakdownKey] ?? '');
      const vals = displayMetrics.map((m) => String(Number(row[metricToRowKey(m)] ?? 0)));
      return [bd, ...vals].join(',');
    });
    const csv = [header.join(','), ...csvRows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'marketing_report.csv';
    a.click();
    URL.revokeObjectURL(url);
  }, [sortedRows, breakdownKey, displayMetrics]);

  // ── Copy to clipboard ──────────────────────────────────────────────────
  const handleCopy = useCallback(() => {
    const header = [breakdownKey, ...displayMetrics.map((m) => METRIC_LABELS[m] ?? m)];
    const tsvRows = sortedRows.map((row) => {
      const bd = String(row[breakdownKey] ?? '');
      const vals = displayMetrics.map((m) => String(Number(row[metricToRowKey(m)] ?? 0)));
      return [bd, ...vals].join('\t');
    });
    const tsv = [header.join('\t'), ...tsvRows].join('\n');
    navigator.clipboard.writeText(tsv).catch(() => { /* ignore */ });
  }, [sortedRows, breakdownKey, displayMetrics]);

  if (rawRows.length === 0) return null;

  return (
    <div className={cn('w-full', className)}>
      {/* ── Toolbar ──────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <span className="text-[11px] text-slate-500 bg-slate-100 px-2.5 py-1 rounded-full font-medium tracking-wide">
            {rawRows.length.toLocaleString()} rows · {displayMetrics.length} metrics
          </span>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setHeatmapEnabled((p) => !p)}
            className={cn(
              'text-[11px] font-medium px-2.5 py-1.5 rounded-lg border transition-all',
              heatmapEnabled
                ? 'bg-indigo-50 border-indigo-200 text-indigo-700'
                : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50',
            )}
          >
            Heatmap
          </button>
          <button
            onClick={() => setBarsEnabled((p) => !p)}
            className={cn(
              'text-[11px] font-medium px-2.5 py-1.5 rounded-lg border transition-all',
              barsEnabled
                ? 'bg-indigo-50 border-indigo-200 text-indigo-700'
                : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50',
            )}
          >
            Bars
          </button>
          <div className="w-px h-5 bg-slate-200" />
          <button
            onClick={handleCopy}
            title="Copy to clipboard"
            className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700
                       bg-white border border-slate-200 rounded-lg px-2.5 py-1.5
                       hover:bg-slate-50 transition-colors"
          >
            <Copy className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={handleExport}
            className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700
                       bg-white border border-slate-200 rounded-lg px-2.5 py-1.5
                       hover:bg-slate-50 transition-colors"
          >
            <Download className="w-3.5 h-3.5" />
            CSV
          </button>
        </div>
      </div>

      {/* ── Table ────────────────────────────────────────────────────────── */}
      <div className="border border-slate-200 rounded-xl overflow-hidden shadow-sm bg-white">
        <div className="overflow-auto max-h-[600px] scrollbar-thin">
          <NormalTable
            rows={pagedRows}
            breakdownKey={breakdownKey}
            displayMetrics={displayMetrics}
            colStats={colStats}
            sort={sort}
            toggleSort={toggleSort}
            heatmapEnabled={heatmapEnabled}
            barsEnabled={barsEnabled}
            cmpLookup={cmpLookup}
          />
        </div>
      </div>

      {/* ── Pagination ─────────────────────────────────────────────────────── */}
      {rawRows.length > PAGE_SIZE_OPTIONS[0] && (
        <div className="flex items-center justify-between mt-3 px-1">
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-slate-400 font-medium">Rows per page</span>
            <select
              value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }}
              className="text-[12px] text-slate-600 bg-white border border-slate-200 rounded-lg
                         px-2 py-1 focus:outline-none focus:ring-2 focus:ring-indigo-500/20
                         focus:border-indigo-300 transition-all"
            >
              {PAGE_SIZE_OPTIONS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-3">
            <span className="text-[11px] text-slate-400 font-medium tabular-nums">
              {page * pageSize + 1}–{Math.min((page + 1) * pageSize, sortedRows.length)} of{' '}
              {sortedRows.length.toLocaleString()}
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="p-1 rounded-md border border-slate-200 text-slate-500
                           hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="p-1 rounded-md border border-slate-200 text-slate-500
                           hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Normal Table ────────────────────────────────────────────────────────────

function NormalTable({
  rows,
  breakdownKey,
  displayMetrics,
  colStats,
  sort,
  toggleSort,
  heatmapEnabled,
  barsEnabled,
  cmpLookup,
}: {
  rows: Array<Record<string, unknown>>;
  breakdownKey: string;
  displayMetrics: string[];
  colStats: Record<string, { min: number; max: number; total: number }>;
  sort: SortState;
  toggleSort: (key: string) => void;
  heatmapEnabled: boolean;
  barsEnabled: boolean;
  cmpLookup: Map<string, Record<string, number>> | null;
}) {
  return (
    <table className="w-full border-collapse min-w-max">
      <thead className="sticky top-0 z-20">
        <tr className="bg-slate-50 border-b border-slate-200">
          <th
            className="sticky left-0 z-30 bg-slate-50
                       text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500
                       border-r border-slate-200 cursor-pointer select-none
                       hover:bg-slate-100 transition-colors min-w-[180px]"
            onClick={() => toggleSort('__breakdown')}
          >
            <span className="flex items-center gap-1.5">
              {breakdownKey.replace(/_/g, ' ')}
              <SortIcon current={sort} column="__breakdown" />
            </span>
          </th>
          {displayMetrics.map((m) => (
            <th
              key={m}
              className="text-right px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500
                         border-r border-slate-100 last:border-r-0
                         cursor-pointer select-none hover:bg-slate-100 transition-colors min-w-[110px]"
              onClick={() => toggleSort(m)}
            >
              <span className="flex items-center justify-end gap-1.5">
                {METRIC_LABELS[m] ?? m}
                <SortIcon current={sort} column={m} />
              </span>
            </th>
          ))}
        </tr>
      </thead>

      <tbody>
        {rows.map((row, i) => {
          const bdValue = String(row[breakdownKey] ?? 'Unknown');
          const rowBg = i % 2 === 0 ? 'bg-white' : 'bg-slate-50';
          return (
            <tr
              key={i}
              className={cn(
                'border-b border-slate-100 last:border-b-0 transition-colors',
                rowBg,
              )}
            >
              <td
                className={cn(
                  'sticky left-0 z-10 px-4 py-2.5',
                  'text-[13px] font-medium text-slate-800',
                  'border-r border-slate-200',
                  rowBg,
                )}
              >
                <span className="truncate block max-w-[260px]" title={bdValue}>
                  {bdValue}
                </span>
              </td>
              {displayMetrics.map((m) => {
                const cmpRow = cmpLookup?.get(bdValue);
                return (
                  <MetricCell
                    key={m}
                    metric={m}
                    value={Number(row[metricToRowKey(m)] ?? 0)}
                    comparisonValue={cmpRow ? cmpRow[metricToRowKey(m)] : undefined}
                    stats={colStats[m]}
                    heatmapEnabled={heatmapEnabled}
                    barsEnabled={barsEnabled}
                  />
                );
              })}
            </tr>
          );
        })}
      </tbody>

      {/* Totals row */}
      <tfoot className="sticky bottom-0 z-20">
        <tr className="bg-slate-50 border-t-2 border-slate-200">
          <td
            className="sticky left-0 z-30 bg-slate-50
                       px-4 py-2.5 text-[12px] font-bold text-slate-700 uppercase tracking-wider
                       border-r border-slate-200"
          >
            Total
          </td>
          {displayMetrics.map((m) => {
            const total = colStats[m]?.total ?? 0;
            // Compute comparison total for this metric across all rows
            let cmpTotal: number | undefined;
            if (cmpLookup) {
              let sum = 0;
              cmpLookup.forEach((colMap) => {
                sum += colMap[metricToRowKey(m)] ?? 0;
              });
              cmpTotal = sum;
            }
            const totalDelta = cmpTotal != null ? getDeltaMkt(total, cmpTotal, m) : null;
            return (
              <td
                key={m}
                className={cn(
                  'px-3 text-right border-r border-slate-100 last:border-r-0',
                  totalDelta ? 'py-1.5' : 'py-2.5',
                )}
              >
                <span
                  className="text-[13px] font-semibold text-slate-800 tabular-nums"
                  style={{ fontFamily: "'JetBrains Mono', ui-monospace, 'Cascadia Code', monospace" }}
                >
                  {formatMetric(m, total)}
                </span>
                {totalDelta && <DeltaIndicatorMkt delta={totalDelta} />}
              </td>
            );
          })}
        </tr>
      </tfoot>
    </table>
  );
}

// ─── Metric Cell ─────────────────────────────────────────────────────────────

function MetricCell({
  metric,
  value,
  comparisonValue,
  stats,
  heatmapEnabled,
  barsEnabled,
}: {
  metric: string;
  value: number;
  comparisonValue?: number;
  stats: { min: number; max: number; total: number } | undefined;
  heatmapEnabled: boolean;
  barsEnabled: boolean;
}) {
  const s = stats ?? { min: 0, max: 0, total: 0 };
  const hm = heatmapEnabled
    ? getHeatmapStyle(value, s.min, s.max)
    : { bg: 'transparent', text: '#334155' };
  const barW = barsEnabled ? getBarWidth(value, s.max) : 0;

  // ROAS colour coding overrides
  const isRoas = metric === 'roas';
  let roasBg = '';
  let roasText = '';
  if (isRoas && !heatmapEnabled && value > 0) {
    if (value > 1) {
      roasBg = 'rgba(16, 185, 129, 0.08)';
      roasText = '#047857';
    } else {
      roasBg = 'rgba(239, 68, 68, 0.08)';
      roasText = '#B91C1C';
    }
  }

  const finalBg = heatmapEnabled ? hm.bg : roasBg || undefined;
  const finalText = heatmapEnabled ? hm.text : (roasText || '#334155');

  // Comparison delta
  const delta = comparisonValue != null
    ? getDeltaMkt(value, comparisonValue, metric)
    : null;

  return (
    <td
      className={cn(
        'relative text-right transition-colors duration-200 border-r border-slate-100/80 last:border-r-0 px-3',
        delta ? 'py-1.5' : 'py-2.5',
      )}
      style={{ backgroundColor: finalBg }}
    >
      {barsEnabled && barW > 0 && (
        <div
          className="absolute top-[6px] bottom-[6px] left-[4px] rounded-[3px] transition-all duration-300"
          style={{
            width: `calc(${barW}% - 8px)`,
            background: isRoas
              ? value > 1
                ? 'linear-gradient(90deg, rgba(16,185,129,0.2), rgba(16,185,129,0.06))'
                : 'linear-gradient(90deg, rgba(239,68,68,0.2), rgba(239,68,68,0.06))'
              : 'linear-gradient(90deg, rgba(99,102,241,0.18), rgba(99,102,241,0.08))',
          }}
        />
      )}
      <span
        className="relative z-[1] text-[13px] tabular-nums"
        style={{
          fontFamily: "'JetBrains Mono', ui-monospace, 'Cascadia Code', monospace",
          color: finalText,
          fontWeight: (isRoas && value > 0) || (stats && value === stats.max) ? 600 : 400,
        }}
      >
        {formatMetric(metric, value)}
      </span>
      {delta && <DeltaIndicatorMkt delta={delta} />}
    </td>
  );
}

// ─── Comparison helpers ──────────────────────────────────────────────────────

interface DeltaInfoMkt {
  pct: number;
  direction: 'up' | 'down' | 'flat';
  /** For cost metrics, "up" is bad (red); for revenue metrics, "up" is good (green) */
  sentiment: 'positive' | 'negative' | 'neutral';
}

/** Metrics where an increase is a negative signal (costs) */
const COST_METRICS = new Set(['cost', 'cpi', 'cpp']);

function getDeltaMkt(current: number, previous: number, metric: string): DeltaInfoMkt | null {
  if (current === previous) return { pct: 0, direction: 'flat', sentiment: 'neutral' };
  const abs = current - previous;
  const pct = previous !== 0 ? (abs / Math.abs(previous)) * 100 : (current > 0 ? Infinity : -Infinity);
  const direction: 'up' | 'down' = abs > 0 ? 'up' : 'down';

  let sentiment: 'positive' | 'negative' | 'neutral';
  if (COST_METRICS.has(metric)) {
    // For cost metrics: up = negative, down = positive
    sentiment = direction === 'up' ? 'negative' : 'positive';
  } else {
    // For revenue/install metrics: up = positive, down = negative
    sentiment = direction === 'up' ? 'positive' : 'negative';
  }

  return { pct, direction, sentiment };
}

function DeltaIndicatorMkt({ delta }: { delta: DeltaInfoMkt }) {
  if (delta.direction === 'flat') return null;

  const isUp = delta.direction === 'up';
  const arrow = isUp ? '▲' : '▼';
  const color = delta.sentiment === 'positive'
    ? 'text-emerald-600'
    : delta.sentiment === 'negative'
      ? 'text-red-500'
      : 'text-slate-400';
  const pctStr = Math.abs(delta.pct) === Infinity
    ? 'new'
    : `${Math.abs(delta.pct).toFixed(1)}%`;

  return (
    <div
      className={cn('relative z-[1] text-[10px] tabular-nums leading-tight mt-0.5', color)}
      style={{ fontFamily: "'JetBrains Mono', ui-monospace, 'Cascadia Code', monospace" }}
    >
      {arrow} {pctStr}
    </div>
  );
}

// ─── Sort indicator ──────────────────────────────────────────────────────────

function SortIcon({ current, column }: { current: SortState; column: string }) {
  if (current.key !== column || !current.dir) {
    return <ArrowUpDown className="w-3 h-3 text-slate-300" />;
  }
  return current.dir === 'asc' ? (
    <ArrowUp className="w-3 h-3 text-indigo-500" />
  ) : (
    <ArrowDown className="w-3 h-3 text-indigo-500" />
  );
}
